/**
 * FELVORA — ACTIVIDAD VULNERABLE · Calibración de la metodología EBR (AV-only).
 *
 * sep-2026 · TEMA 3 de la auditoría `AUDITORIA_AV_CANAL_OC_AREAS_EBR.md`: hasta hoy el editor
 * de la metodología (`components/riesgo/RiesgoEBR.jsx`) vivía SOLO en la pantalla `/pld`, cuyo
 * grupo de menú es `GIROS_SOFOM_ONLY`. El Oficial de Cumplimiento de una entidad de Actividad
 * Vulnerable no tenía dónde calibrar su EBR.
 *
 * Este componente es NUEVO y AV-only: consume los MISMOS endpoints ya sellados
 * (`GET/PUT /api/riesgo/metodologia`, `/firmar`, `/borrador/cancelar`, `/historial`,
 * `/{v}/evidencia`) y no toca una línea del editor de SOFOM.
 *
 * Estructura FIJA (los 5 factores y sus subvariables vienen del backend) · CALIBRACIÓN
 * editable: ponderaciones que deben sumar 100, umbrales del semáforo y escalas por variable.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Scale, Save, Loader2, Lock, PenLine, XCircle, History, FileDown, ShieldCheck } from "lucide-react";

import { api, apiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { AvEbrEscalas, DEFAULT_ESCALAS_AV } from "@/components/riesgo/av/AvEbrEscalas";
import { AvEbrCatalogos } from "@/components/riesgo/av/AvEbrCatalogos";

const ROLES_CALIBRAN = ["admin_entidad", "oficial_cumplimiento", "cumplimiento_inmo"];
const ROLES_FIRMAN = ["admin_entidad", "oficial_cumplimiento"];

const ESTADO_LABEL = {
  primera_config: "Línea base pendiente de sellar",
  revision_anual: "En ventana de revisión anual",
  revision_enr: "En revisión por nueva Evaluación Nacional de Riesgos",
  bloqueada: "Sellada y bloqueada",
};

export default function AvEbrCalibracion() {
  const { user } = useAuth();
  const role = user?.role;
  const puedeEditar = ROLES_CALIBRAN.includes(role);

  const [met, setMet] = useState(null);
  const [escalas, setEscalas] = useState(DEFAULT_ESCALAS_AV);
  const [historial, setHistorial] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const m = await api.get("/riesgo/metodologia").then((r) => r.data);
      setMet({ ...m, umbrales: m.umbrales || { medio: 40, alto: 70, eleva_subfactor: 67 } });
      setEscalas({
        tipo_persona: { ...DEFAULT_ESCALAS_AV.tipo_persona, ...(m.escalas?.tipo_persona || {}) },
        monto_frecuencia: { ...DEFAULT_ESCALAS_AV.monto_frecuencia, ...(m.escalas?.monto_frecuencia || {}) },
        uso_efectivo: { ...DEFAULT_ESCALAS_AV.uso_efectivo, ...(m.escalas?.uso_efectivo || {}) },
        medio_pago: { ...DEFAULT_ESCALAS_AV.medio_pago, ...(m.escalas?.medio_pago || {}) },
        canal: { ...DEFAULT_ESCALAS_AV.canal, ...(m.escalas?.canal || {}) },
      });
      const h = await api.get("/riesgo/metodologia/historial").then((r) => r.data.versiones).catch(() => []);
      setHistorial(h || []);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!met) {
    return <div className="p-6 text-sm text-slate-500" data-testid="av-ebr-calibracion-cargando">Cargando metodología EBR…</div>;
  }

  const estado = met.estado_edicion || "primera_config";
  const bloqueada = estado === "bloqueada";
  const enPrimeraConfig = estado === "primera_config";
  const borrador = met.borrador || null;
  const firmas = borrador?.firmas || {};
  const rolPendiente = firmas.admin_entidad && !firmas.oficial_cumplimiento
    ? "oficial_cumplimiento"
    : firmas.oficial_cumplimiento && !firmas.admin_entidad ? "admin_entidad" : null;
  const esFirmantePendiente = !!(rolPendiente && role === rolPendiente && borrador);
  const editable = puedeEditar && !bloqueada
    && !(enPrimeraConfig && role !== "admin_entidad")
    && !esFirmantePendiente;

  const factores = esFirmantePendiente && borrador?.factores ? borrador.factores : (met.factores || []);
  const umbrales = esFirmantePendiente && borrador?.umbrales ? borrador.umbrales : met.umbrales;
  const suma = factores.reduce((a, f) => a + Number(f.ponderacion || 0), 0);
  const umbralesOk = Number(umbrales.medio) >= 0 && Number(umbrales.medio) < Number(umbrales.alto)
    && Number(umbrales.alto) <= 100 && Number(umbrales.eleva_subfactor) >= 0
    && Number(umbrales.eleva_subfactor) <= 100;

  const setPond = (clave, val) => setMet((p) => ({
    ...p, factores: p.factores.map((f) => f.clave === clave ? { ...f, ponderacion: Number(val) } : f),
  }));
  const setUmbral = (k, val) => setMet((p) => ({ ...p, umbrales: { ...p.umbrales, [k]: Number(val) } }));

  const guardar = async () => {
    if (suma !== 100) { toast.error(`Las ponderaciones deben sumar 100 % (actual: ${suma} %).`); return; }
    if (!umbralesOk) { toast.error("Umbrales incoherentes: debe cumplirse 0 ≤ Medio < Alto ≤ 100."); return; }
    setSaving(true);
    try {
      const { data } = await api.put("/riesgo/metodologia", {
        factores: met.factores, umbrales: met.umbrales, escalas,
      });
      if (data?.sellada_por_doble_firma) toast.success("Metodología sellada por doble firma. Bloqueada 12 meses.");
      else if (data?.requiere_firma_pendiente) toast.success(`Propuesta guardada. Falta la firma de ${data.rol_pendiente}.`);
      else toast.success("Calibración de la metodología EBR guardada.");
      await load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(d && typeof d === "object" ? d.message : apiError(e));
    } finally { setSaving(false); }
  };

  const firmar = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/riesgo/metodologia/firmar");
      toast.success(data?.sellada_por_doble_firma
        ? "Metodología sellada por doble firma."
        : `Tu firma quedó registrada. Falta la firma de ${data.rol_pendiente}.`);
      await load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const cancelar = async () => {
    setSaving(true);
    try {
      await api.post("/riesgo/metodologia/borrador/cancelar");
      toast.success("Borrador de revisión cancelado.");
      await load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const descargarEvidencia = async (ver) => {
    try {
      const token = localStorage.getItem("cos_token");
      // sep-2026 · El vertical AV descarga la evidencia COMPLETA: el PDF de siempre + el anexo
      // con mitigantes e indicadores 139 Quáter / 400 Bis. El endpoint sellado de SOFOM
      // (`/riesgo/metodologia/{id}/evidencia`) no se toca ni se usa aquí.
      const res = await fetch(`${API}/av/ebr/metodologia/${ver.id}/evidencia`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("No se pudo generar el PDF de evidencia.");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `Metodologia_EBR_v${ver.version}_completa.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(a.href);
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-5" data-testid="av-ebr-calibracion">
      {/* Estado del candado y firmas */}
      <Card className={bloqueada ? "border-emerald-200 bg-emerald-50/60" : "border-amber-200 bg-amber-50/60"}>
        <CardContent className="flex flex-wrap items-start justify-between gap-3 p-4">
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
              {bloqueada ? <Lock className="h-4 w-4 text-emerald-700" /> : <ShieldCheck className="h-4 w-4 text-amber-700" />}
              <span data-testid="av-ebr-estado">{ESTADO_LABEL[estado] || estado}</span>
              {met.version && <Badge variant="outline" data-testid="av-ebr-version">versión {met.version}</Badge>}
            </div>
            <p className="mt-1 text-xs text-slate-600">
              {bloqueada
                ? `Próxima edición disponible: ${met.desbloqueable_desde || "por definir"}. Los catálogos de alto riesgo siguen editables.`
                : "Los cambios de ponderaciones, umbrales y escalas quedan versionados y firmados (LFPIORPI Art. 18 fr. VII)."}
            </p>
            {borrador && (
              <p className="mt-1 text-xs font-medium text-slate-700" data-testid="av-ebr-borrador-firmas">
                Borrador de revisión: firmado por {Object.keys(firmas).join(", ") || "nadie"}
                {rolPendiente ? ` · falta ${rolPendiente}` : ""}
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {borrador && rolPendiente === role && ROLES_FIRMAN.includes(role) && (
              <Button size="sm" onClick={firmar} disabled={saving} data-testid="av-ebr-firmar-btn"
                className="bg-emerald-600 text-white hover:bg-emerald-700">
                {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <PenLine className="mr-1.5 h-4 w-4" />}
                Firmar como {role}
              </Button>
            )}
            {borrador && puedeEditar && (
              <Button size="sm" variant="outline" onClick={cancelar} disabled={saving} data-testid="av-ebr-cancelar-borrador-btn">
                <XCircle className="mr-1.5 h-4 w-4" /> Cancelar borrador
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Ponderaciones y umbrales */}
      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Scale className="h-4 w-4 text-primary" /> Ponderación de los 5 factores obligatorios
          </CardTitle>
          <div className="flex items-center gap-3">
            <span className={`text-sm font-semibold ${suma === 100 ? "text-emerald-600" : "text-red-600"}`} data-testid="av-ebr-suma">
              Suma: {suma} %
            </span>
            {editable && (
              <Button size="sm" onClick={guardar} disabled={saving || suma !== 100 || !umbralesOk}
                data-testid="av-ebr-guardar-btn" className="bg-primary hover:bg-sidebar-hover">
                {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />}
                {enPrimeraConfig ? "Sellar línea base" : "Guardar calibración"}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {factores.map((f) => (
              <div key={f.clave} className="rounded-md border border-slate-200 bg-slate-50/60 p-3" data-testid={`av-ebr-factor-${f.clave}`}>
                <p className="text-sm font-medium text-slate-800">{f.nombre}</p>
                <p className="mb-2 text-xs text-slate-500">
                  {(f.subvariables || []).map((s) => s.nombre).join(" · ")}
                </p>
                <div className="flex items-center gap-1.5">
                  <Input type="number" min="0" max="100" className="h-8 w-24"
                    data-testid={`av-ebr-pond-${f.clave}`} value={f.ponderacion}
                    disabled={!editable} onChange={(e) => setPond(f.clave, e.target.value)} />
                  <span className="text-sm text-slate-500">%</span>
                </div>
              </div>
            ))}
          </div>

          <div className="rounded-md border border-slate-200 bg-slate-50/60 p-3" data-testid="av-ebr-umbrales">
            <p className="mb-2 text-sm font-medium text-slate-800">Umbrales del semáforo (puntos)</p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              {[
                { k: "medio", label: "Riesgo Medio desde" },
                { k: "alto", label: "Riesgo Alto desde" },
                { k: "eleva_subfactor", label: "Subvariable que eleva desde" },
              ].map(({ k, label }) => (
                <div key={k}>
                  <p className="mb-1 text-xs text-slate-500">{label}</p>
                  <Input type="number" min="0" max="100" className="h-8 w-24"
                    data-testid={`av-ebr-umbral-${k}`} value={umbrales[k]}
                    disabled={!editable} onChange={(e) => setUmbral(k, e.target.value)} />
                </div>
              ))}
            </div>
            <p className={`mt-2 text-xs ${umbralesOk ? "text-slate-400" : "font-medium text-red-600"}`} data-testid="av-ebr-umbrales-hint">
              Debe cumplirse 0 ≤ Medio &lt; Alto ≤ 100.
            </p>
          </div>

          <AvEbrEscalas escalas={escalas} onChange={setEscalas} disabled={!editable} />
        </CardContent>
      </Card>

      <AvEbrCatalogos />

      {/* Historial y evidencia */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <History className="h-4 w-4 text-slate-500" /> Versiones firmadas de la metodología
          </CardTitle>
        </CardHeader>
        <CardContent>
          {historial.length === 0 ? (
            <p className="text-sm italic text-slate-400" data-testid="av-ebr-historial-vacio">
              Aún no hay versiones sellladas.
            </p>
          ) : (
            <div className="divide-y divide-slate-100" data-testid="av-ebr-historial">
              {historial.map((v) => (
                <div key={v.id} className="flex flex-wrap items-center justify-between gap-2 py-2" data-testid={`av-ebr-version-${v.version}`}>
                  <div className="text-sm">
                    <span className="font-medium text-slate-800">Versión {v.version}</span>
                    {v.vigente && <Badge className="ml-2 bg-emerald-100 text-emerald-800">vigente</Badge>}
                    <span className="ml-2 text-xs text-slate-500">
                      {v.fecha || (v.created_at || "").slice(0, 10)}
                      {v.sellado_por || v.aprobado_por
                        ? ` · sellada por ${v.sellado_por || v.aprobado_por}`
                        : ""}
                    </span>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => descargarEvidencia(v)}
                    data-testid={`av-ebr-evidencia-${v.version}`}>
                    <FileDown className="mr-1.5 h-4 w-4" /> Evidencia PDF
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
